import { useState, useEffect, useCallback } from 'react';
import type { UIEvent } from 'react';
import { Typography, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useBackButton } from '../hooks/useBackButton';
import { useTelegramViewport } from '../hooks/useTelegramViewport';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import PullToRefreshIndicator from '../components/shared/PullToRefreshIndicator';
import CustomerRow from '../components/customers/CustomerRow';
import CustomersSkeleton from '../components/customers/CustomersSkeleton';
import CustomersEmpty from '../components/customers/CustomersEmpty';
import { businessApi } from '../api/businessApi';
import { useBusinessStore } from '../store/businessStore';
import type { Customer } from '../types/business';

const PAGE_SIZE = 20;

interface Props {
  onBack: () => void;
}

export default function CustomersPage({ onBack }: Props) {
  const { t, i18n } = useTranslation('owner');
  const { selectedBusiness } = useBusinessStore();
  const tenantId = selectedBusiness?.id;
  const viewportHeight = useTelegramViewport();

  const stableOnBack = useCallback(onBack, []);
  useBackButton(stableOnBack);

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);

  const loadFirstPage = useCallback(async () => {
    if (!tenantId) return;
    try {
      const data = await businessApi.getCustomers(tenantId, 0, PAGE_SIZE);
      setCustomers(data.content);
      setTotalElements(data.totalElements);
      setHasMore(data.totalPages > 1);
      setPage(0);
    } catch {
      // silent — empty list
    } finally {
      setInitialLoaded(true);
    }
  }, [tenantId]);

  const loadNextPage = useCallback(async () => {
    if (!tenantId || loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const data = await businessApi.getCustomers(tenantId, nextPage, PAGE_SIZE);
      setCustomers((prev) => [...prev, ...data.content]);
      setTotalElements(data.totalElements);
      setHasMore(nextPage + 1 < data.totalPages);
      setPage(nextPage);
    } catch {
      // silent
    } finally {
      setLoadingMore(false);
    }
  }, [tenantId, loadingMore, hasMore, page]);

  useEffect(() => {
    void loadFirstPage();
  }, [loadFirstPage]);

  const handleRefresh = useCallback(async () => {
    setInitialLoaded(false);
    await loadFirstPage();
  }, [loadFirstPage]);

  const { containerRef, pullDistance, isRefreshing, progress } =
    usePullToRefresh<HTMLDivElement>({ onRefresh: handleRefresh });

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (nearBottom && hasMore && !loadingMore) {
      void loadNextPage();
    }
  };

  return (
    <div
      style={{
        height: viewportHeight,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'var(--bl-bg, #f5f7fa)',
      }}
    >
      {/* Header */}
      <div
        style={{
          flexShrink: 0,
          padding: '12px 16px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          background: 'var(--bl-surface, #fff)',
          borderBottom: '1px solid var(--bl-divider, #ebebeb)',
          paddingTop: 'max(12px, env(safe-area-inset-top, 12px))',
        }}
      >
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          style={{
            width: 36,
            height: 36,
            borderRadius: 11,
            background: 'var(--bl-surface-soft, #f0f4f8)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 15,
            color: 'var(--bl-text-strong, #0f172a)',
            flexShrink: 0,
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <ArrowLeftOutlined />
        </button>

        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <Typography.Text
            style={{
              fontSize: 18,
              fontWeight: 800,
              color: 'var(--bl-text-strong, #0f172a)',
              letterSpacing: -0.3,
            }}
          >
            {t('customers.title')}
          </Typography.Text>
          {totalElements > 0 && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--bl-accent, #3390ec)',
                background: 'var(--bl-accent-soft, rgba(51,144,236,0.10))',
                border: '1px solid rgba(51,144,236,0.20)',
                borderRadius: 999,
                padding: '2px 10px',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {t('customers.countLabel', { count: totalElements })}
            </span>
          )}
        </div>
      </div>

      {/* List */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingBottom: 'env(safe-area-inset-bottom, 16px)',
          position: 'relative',
        }}
      >
        <PullToRefreshIndicator
          pullDistance={pullDistance}
          isRefreshing={isRefreshing}
          progress={progress}
        />

        {/* Initial loading */}
        {!initialLoaded && !isRefreshing && <CustomersSkeleton />}

        {/* Refresh placeholder — show skeleton during pull-to-refresh */}
        {isRefreshing && customers.length === 0 && <CustomersSkeleton />}

        {/* Empty state */}
        {initialLoaded && !isRefreshing && customers.length === 0 && <CustomersEmpty />}

        {/* Customer list */}
        {customers.length > 0 && (
          <div
            style={{
              padding: '12px 16px 4px',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            {customers.map((customer, idx) => {
              const delayClass =
                idx < 3 ? 'bl-animate-in' :
                idx < 6 ? 'bl-animate-in bl-animate-in-delay-1' :
                idx < 9 ? 'bl-animate-in bl-animate-in-delay-2' :
                idx < 12 ? 'bl-animate-in bl-animate-in-delay-3' : '';
              return (
                <div key={customer.id} className={delayClass}>
                  <CustomerRow
                    customer={customer}
                    language={i18n.language}
                    fallbackName={t('customers.fallbackName')}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Load more spinner */}
        {loadingMore && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
            <Spin size="small" />
          </div>
        )}

        {/* All loaded */}
        {!hasMore && customers.length > 0 && !loadingMore && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              padding: '14px 16px 20px',
            }}
          >
            <div style={{ height: 1, flex: 1, maxWidth: 40, background: 'var(--bl-divider, #e5e7eb)' }} />
            <Typography.Text style={{ fontSize: 11, color: 'var(--bl-text-muted, #9ca3af)' }}>
              {t('customers.allShown')}
            </Typography.Text>
            <div style={{ height: 1, flex: 1, maxWidth: 40, background: 'var(--bl-divider, #e5e7eb)' }} />
          </div>
        )}
      </div>
    </div>
  );
}
